import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import dateRenderer from '@elementary/common-ui/components/cellRenderers/dateRenderer';
import statusFilterRenderer from '@elementary/common-ui/components/cellRenderers/statusFilterRenderer';
import { statusValueRenderer } from '@elementary/common-ui/components/cellRenderers/statusRenderer';
import testSubTypeRenderer from '@elementary/common-ui/components/cellRenderers/testSubTypeRenderer';
import testTypeFilterRenderer from '@elementary/common-ui/components/cellRenderers/testTypeFilterRenderer';
import Table, {
  Column,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import { DateRangeValue } from '@elementary/common-ui/components/DateRangePicker';
import {
  DRAWER_TYPE,
  PARENT_TEST_ID,
  TEST_ID,
  TEST_OVERVIEW_SIDEPANEL_TAB,
  TEST_OVERVIEW_TAB_EXECUTION_INFO,
} from '@elementary/common-ui/constants/urlParams';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import dayjs from 'dayjs';
import { Typography } from '@mui/material';
import { space } from '@elementary/common-ui/style/utils';
import { TestOverview, Status } from '../../types';

const STORAGE_KEY = 'test_configuration_panel_latest_results';

const Root = styled(FlexColumn)`
  height: 100%;
`;

const TableContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
`;

const Toolbar = styled(Flex)`
  justify-content: flex-end;
  padding: ${space('xs', 'sm')};
`;

type RowData = {
  id: string;
  subTestUniqueId: string;
  column: string;
  subType: string;
  qualityDimension: string;
  failures: number | null;
  status: Status;
  lastRun: string;
};

const columns: Column<RowData>[] = [
  {
    id: 'column',
    header: 'Column',
    sortable: false,
    renderer: ({ column }) => <OverflowTooltip>{column}</OverflowTooltip>,
  },
  {
    id: 'subType',
    header: 'Test Type',
    sortable: false,
    renderer: ({ subType }) => testSubTypeRenderer(subType),
    filterRenderer: testTypeFilterRenderer,
  },
  {
    id: 'qualityDimension',
    header: 'Quality Dimension',
    sortable: false,
    defaultUntoggled: true,
    renderer: ({ qualityDimension }) => (
      <OverflowTooltip>{qualityDimension}</OverflowTooltip>
    ),
  },
  {
    id: 'failures',
    header: 'Failed Rows',
    filterable: false,
    sortable: false,
    renderer: ({ failures }) => (
      <OverflowTooltip>
        {failures != null ? failures.toLocaleString() : '-'}
      </OverflowTooltip>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    renderer: ({ status }) => statusValueRenderer(status),
    filterRenderer: statusFilterRenderer,
  },
  {
    id: 'lastRun',
    header: 'Last Run',
    filterable: false,
    sortable: false,
    renderer: ({ lastRun }) => dateRenderer(lastRun),
  },
];

type Props = {
  executions: TestOverview[];
  dateRange?: DateRangeValue;
};

const LatestResultsTab = ({ executions, dateRange }: Props) => {
  const { selectedId } = useSidePanelParams();
  const [, setSearchParams] = useSearchParams();

  const handleRowClick = useCallback(
    (row: RowData) => {
      setSearchParams((prev) => {
        prev.set(PARENT_TEST_ID, selectedId ?? '');
        prev.set(TEST_ID, row.subTestUniqueId);
        prev.set(DRAWER_TYPE, 'test');
        prev.set(TEST_OVERVIEW_SIDEPANEL_TAB, TEST_OVERVIEW_TAB_EXECUTION_INFO);
        return prev;
      });
    },
    [selectedId, setSearchParams],
  );

  const data = useMemo<RowData[]>(
    () =>
      executions
        .filter(({ execution }) => {
          if (!dateRange) return true;
          const ts = dayjs(execution.executionTimestamp);
          return ts.isAfter(dateRange.start) && ts.isBefore(dateRange.end);
        })
        .map(({ execution }) => {
          const columnName =
            execution.resource?.type === 'COLUMN'
              ? execution.resource.name.split('.').slice(-1)[0]?.toLowerCase()
              : '-';

          return {
            id: execution.id,
            subTestUniqueId: execution.subTestUniqueId,
            column: columnName,
            subType: execution.subType,
            qualityDimension: execution.qualityDimension ?? '-',
            failures: execution.failures,
            status: execution.status,
            lastRun: execution.executionTimestamp,
          };
        }),
    [executions, dateRange],
  );

  return (
    <Root>
      <Toolbar>
        <ToggleColumnsMenu columns={columns} localStorageKey={STORAGE_KEY} />
      </Toolbar>
      <TableContainer>
        <Table
          defaultSortKey="status"
          columns={columns}
          data={data}
          idKey="id"
          localStorageKey={STORAGE_KEY}
          onRowClick={handleRowClick}
          emptyStateRenderer={() => (
            <FlexCenter sx={{ width: '100%' }}>
              <Typography sx={{ color: 'content.tertiary' }}>
                {dateRange?.end
                  ? `No test runs in the past ${Math.round(
                      dateRange.end.diff(dateRange.start, 'day', true),
                    )} days`
                  : 'No test runs to display'}
              </Typography>
            </FlexCenter>
          )}
        />
      </TableContainer>
    </Root>
  );
};

export default LatestResultsTab;
